import OrderDTO from "@/dto/order/OrderDTO";
import OrderStatus from "@/enum/OrderStatus";
import OrderTag from "@/enum/OrderTag";
import { orderSettingStore } from "@/store/hooks";
import { UndeliverableArea } from "./api/setting/unDeliverableArea";

export const FILTER_UNDELIVERABLE = "__FILTER_UNDELIVERABLE__";

export const SELECT_UNDELIVERABLE = "__SELECT_UNDELIVERABLE__";

export function getOriginUndeliverableAreaId(key: string) {
    if (key.includes(FILTER_UNDELIVERABLE)) {
        return Number(key.split(FILTER_UNDELIVERABLE)[1]);
    } else {
        return Number(key.split(SELECT_UNDELIVERABLE)[1]);
    }
}

export function getUndeliverableAreaParams(rawUndeliverableAreaIds?: string[]) {
    if (!rawUndeliverableAreaIds?.length) return undefined;

    const isExclude = rawUndeliverableAreaIds[0].includes(FILTER_UNDELIVERABLE);

    const filterUndeliverableAreaList = orderSettingStore.undeliverableAreaList.filter(({ id }) => {
        return (
            rawUndeliverableAreaIds.findIndex(
                (rawUndeliverableAreaId) => getOriginUndeliverableAreaId(rawUndeliverableAreaId) === id,
            ) > -1
        );
    });

    const [provinces, cities, districts] = getUndeliverableAreaCodeGroup(filterUndeliverableAreaList);

    const undeliverableArea = {
        excludedProvinces: isExclude ? provinces : undefined,
        excludedCities: isExclude ? cities : undefined,
        excludedDistricts: isExclude ? districts : undefined,
        includedProvinces: !isExclude ? provinces : undefined,
        includedCities: !isExclude ? cities : undefined,
        includedDistricts: !isExclude ? districts : undefined,
    };

    return undeliverableArea;
}

export function getUndeliverableAreaCodes(undeliverableAreas: UndeliverableArea[]): UndeliverableAreaCode[] {
    const undeliverableAreaCodes: UndeliverableAreaCode[] = [];

    undeliverableAreas.forEach((undeliverableArea) => {
        const { includedAreas } = undeliverableArea;

        if (!undeliverableArea.enable) {
            return;
        }

        try {
            const undeliverableAreaCode = new UndeliverableAreaCode(undeliverableArea);

            const jsonObject = JSON.parse(includedAreas);

            Object.keys(jsonObject).forEach((provinceCode) => {
                const value = jsonObject[provinceCode];

                if (value === "ALL") {
                    undeliverableAreaCode.addProvinceCode(provinceCode);
                    return; // continue
                }

                value.split(";").forEach((it) => {
                    const cityArea: string[] = it.split(":");

                    const cityCode: string = cityArea?.[0];
                    const districtCodes: string = cityArea?.[1];

                    if (districtCodes == "ALL") {
                        undeliverableAreaCode.addCityCode(cityCode);
                    } else {
                        districtCodes?.split(",")?.forEach((districtCode) => {
                            undeliverableAreaCode.addDistrictCode(districtCode);
                        });
                    }
                });
            });

            undeliverableAreaCodes.push(undeliverableAreaCode);
        } catch (e) {
            console.error("fail to init undeliverable area cache", e);
        }
    });

    return undeliverableAreaCodes;
}

/** 构建订单搜索不可达的参数 */
/** ①:广东省 广州市 天河区 ②广东省 广州市 黄浦区 => [[],[],[天河code,黄埔code]] */
/** ①:广东省 广州市 ALL ②广东省 广州市 黄浦区 ③广东省 佛山市 南海区 => [[],[广州code],[南海code]] */
export function getUndeliverableAreaCodeGroup(undeliverableAreas: UndeliverableArea[]): [number[], number[], number[]] {
    const undeliverableProvinceCode = new Set();

    const undeliverableCityCode = new Set();

    const undeliverableDistrictCode = new Set();

    undeliverableAreas.forEach((undeliverableArea) => {
        const { includedAreas } = undeliverableArea;

        if (!undeliverableArea.enable) {
            return;
        }

        try {
            const jsonObject = JSON.parse(includedAreas);

            Object.keys(jsonObject).forEach((provinceCode) => {
                const value = jsonObject[provinceCode];

                if (undeliverableProvinceCode.has(provinceCode)) {
                    return;
                }

                if (value === "ALL") {
                    undeliverableProvinceCode.add(provinceCode);
                    return;
                }

                value.split(";").forEach((it) => {
                    const cityArea: string[] = it.split(":");

                    const cityCode: string = cityArea?.[0];
                    const districtCodes: string = cityArea?.[1];

                    if (undeliverableCityCode.has(cityCode)) return;

                    if (districtCodes == "ALL") {
                        undeliverableCityCode.add(cityCode);
                    } else {
                        districtCodes?.split(",")?.forEach((districtCode) => {
                            if (undeliverableDistrictCode.has(districtCode)) return;

                            undeliverableDistrictCode.add(districtCode);
                        });
                    }
                });
            });
        } catch (e) {
            console.error("fail to init undeliverable area cache", e);
        }
    });

    const undeliverableProvinceCodeList = [...undeliverableProvinceCode].map(Number);
    const undeliverableCityCodeList = [...undeliverableCityCode].map(Number);
    const undeliverableDistrictCodeList = [...undeliverableDistrictCode].map(Number);

    return [undeliverableProvinceCodeList, undeliverableCityCodeList, undeliverableDistrictCodeList];
}

/**
 * 不可达地区检测编码类
 */
export class UndeliverableAreaCode {
    constructor(public originalData: UndeliverableArea) {}

    provinceCodes: string[] = [];

    cityCodes: string[] = [];

    districtCodes: string[] = [];

    addProvinceCode(provinceCode: string) {
        if (provinceCode) {
            this.provinceCodes.push(provinceCode.toString());
        }
    }

    addCityCode(cityCode: string) {
        if (cityCode) {
            this.cityCodes.push(cityCode.toString());
        }
    }

    addDistrictCode(districtCode: string) {
        if (districtCode) {
            this.districtCodes.push(districtCode.toString());
        }
    }

    containsProvinceCode(provinceCode: string | number) {
        if (!provinceCode) {
            return false;
        }

        return this.provinceCodes.includes(provinceCode.toString());
    }

    containsCityCode(cityCode: string | number) {
        if (!cityCode) {
            return false;
        }

        return this.cityCodes.includes(cityCode.toString());
    }

    containsDistrictCode(districtCode: string | number) {
        if (!districtCode) {
            return false;
        }

        return this.districtCodes.includes(districtCode.toString());
    }

    /**
     * 检查订单是否为不可达地址
     */
    checkUndeliverable(order: OrderDTO) {
        if (order.status !== OrderStatus.PENDING_SHIPMENT) {
            return null;
        }

        const { name: undeliverableReason, shopIds, enable } = this.originalData;

        if (!enable) {
            return null;
        }

        /** shopIds为空或者长度为0代表所有店铺可以使用 */
        const isAllowedShop =
            !shopIds ||
            (shopIds && shopIds.length === 0) ||
            (shopIds && shopIds.length > 0 && shopIds.includes(order.shopId));

        if (!isAllowedShop) return null;

        /** 新疆中转+省市区=陕西省咸阳市秦都区或陕西省咸阳市渭城区 不能为不可达订单 */
        if (order.tags?.includes(OrderTag.PDD_XINJIANG_CONSOLIDATE)) {
            if (
                order.consignee &&
                order.consignee.province === "陕西省" &&
                order.consignee.city === "咸阳市" &&
                (order.consignee.district === "秦都区" || order.consignee.district === "渭城区")
            ) {
                return null;
            }
        }

        if (
            this.containsProvinceCode(order.provinceCode) ||
            this.containsCityCode(order.cityCode) ||
            this.containsDistrictCode(order.districtCode)
        ) {
            return undeliverableReason;
        }

        return null;
    }
}
